/* eslint-disable react-refresh/only-export-components */
import { observer } from 'mobx-react-lite';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  AreaChart,
  Area,
} from 'recharts';
import { useNewClientsController } from '@/controllers/report/useNewClientsController';
import type { GroupBy, NewAccountsReportItem } from '@/models/report/types';
import { cn } from '@/lib/utils';
import { chartColors } from '@/design/tokens';
import { ChartTooltip } from '@/components/charts/ChartTooltip';
import { ReportBarChart } from '@/components/reports/ReportBarChart';
import {
  DateRangeInputs,
  ReportExportMenu,
  ReportFilters,
  ReportLoadButton,
} from '@/components/reports/ReportFilters';
import { ReportTable } from '@/components/reports/ReportTable';
import { StatsGrid } from '@/components/reports/StatsGrid';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { AsyncState } from '@/components/ui/AsyncState';
import { ReportPageHeader } from '@/components/layout/ReportPageHeader';
import { formatMoney, formatThousandsTick, parseNumber } from '@/lib/format';
import { formatDateOnly } from '@/lib/dateUtils';
import { exportNewClientsToExcel } from '@/lib/exportExcel';
import { exportNewClientsToGoogleSheets } from '@/lib/exportGoogleSheets';
import { paymentStatusCls } from '@/lib/paymentStatus';

const C = chartColors;

const groupByOptions: { value: GroupBy; label: string }[] = [
  { value: 'month', label: 'По месяцам регистрации' },
  { value: 'year', label: 'По годам регистрации' },
  { value: 'country', label: 'По странам' },
  { value: 'status', label: 'По статусам' },
];

function Row({ item, currency }: { item: NewAccountsReportItem; currency: string }) {
  return (
    <div className='grid grid-cols-12 gap-2 px-4 py-3 items-center text-center hover:bg-muted/20 transition-colors border-b border-border/10 last:border-0'>
      <div className='col-span-2 font-mono text-xs text-muted-foreground text-center'>
        {formatDateOnly(item.date_reg)}
      </div>
      <div className='col-span-1 text-xs text-foreground text-center truncate'>
        {item.account_name}
      </div>
      <div
        className='col-span-1 font-mono text-xs text-muted-foreground text-center truncate'
        title={item.login}
      >
        {item.login}
      </div>
      <div className='col-span-1 font-mono text-xs text-muted-foreground text-center'>
        {item.phone}
      </div>
      <div className='col-span-1 text-xs text-foreground text-center'>{item.country_name}</div>
      <div className='col-span-1 text-xs text-muted-foreground text-center'>
        {item.account_type_name}
      </div>
      <div className='col-span-1 font-mono text-xs text-muted-foreground text-center'>
        {formatDateOnly(item.date_payed_tariff)}
      </div>
      <div className='col-span-1 flex justify-center'>
        <span
          className={cn(
            'text-xs px-2 py-0.5 rounded-lg border font-medium',
            parseNumber(item.count_cameras) > 0
              ? 'bg-primary/10 border-primary/20 text-primary'
              : 'bg-muted/40 border-border/30 text-muted-foreground'
          )}
        >
          {item.count_cameras ?? 0}
        </span>
      </div>
      <div className='col-span-1 flex justify-center'>
        <span
          className={cn(
            'text-xs px-2 py-0.5 rounded-lg border font-medium',
            paymentStatusCls(item.status_name)
          )}
        >
          {item.status_name}
        </span>
      </div>
      <div className='col-span-1 text-xs font-semibold text-center text-foreground'>
        {formatMoney(item.full_abon_plata, currency)}
      </div>
      <div className='col-span-1 text-xs font-semibold text-center text-foreground'>
        {formatMoney(item.all_cameras_abon_plata, currency)}
      </div>
    </div>
  );
}

export const NewClientsReportPage = observer(function NewClientsReportPage() {
  const newClients = useNewClientsController();

  const chartData = newClients.monthlyChartData();
  const paymentCharts = newClients.paymentCharts;

  // Валюты стран не складываются: «Сумма оплат» — отдельно по каждой стране.
  const paymentStats =
    newClients.paymentTotals.length > 0
      ? newClients.paymentTotals.map((total) => ({
          label: `Сумма оплат · ${total.label}`,
          value: formatMoney(total.amount, total.currency),
          cls: 'text-foreground',
        }))
      : [{ label: 'Сумма оплат', value: formatMoney(0), cls: 'text-foreground' }];
  const stats = [
    { label: 'Всего клиентов', value: newClients.totalClients, cls: 'text-foreground' },
    { label: 'Активных', value: newClients.activeClients, cls: 'text-green-500' },
    { label: 'Перешли на платный', value: newClients.paidClients, cls: 'text-primary' },
    { label: 'Всего камер', value: newClients.totalCameras, cls: 'text-foreground' },
    ...paymentStats,
  ];
  const tableHeaders = [
    { label: 'Дата регистрации', className: 'col-span-2 whitespace-nowrap' },
    { label: 'Аккаунт', className: 'col-span-1' },
    { label: 'Логин', className: 'col-span-1' },
    { label: 'Телефон', className: 'col-span-1 text-center' },
    { label: 'Страна', className: 'col-span-1 text-center' },
    { label: 'Тип', className: 'col-span-1 text-center' },
    { label: 'Платный с', className: 'col-span-1 text-center' },
    { label: 'Камер', className: 'col-span-1 text-center' },
    { label: 'Статус', className: 'col-span-1 text-center' },
    { label: 'Абонплата', className: 'col-span-1 text-center' },
    { label: 'Абонплата камер', className: 'col-span-1 text-center' },
  ];

  return (
    <div className='min-h-screen bg-background'>
      <ReportPageHeader />

      <div className='px-6 pb-12'>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className='mb-6'>
            <h1 className='text-2xl font-medium tracking-wider text-foreground'>
              Отчёт по новым клиентам
            </h1>
            <p className='text-sm text-muted-foreground mt-0.5'>
              Анализ новых клиентов за выбранный период
            </p>
          </div>

          {/* Фильтры */}
          <ReportFilters className='items-center'>
            <DateRangeInputs
              start={newClients.store.dateStart}
              end={newClients.store.dateEnd}
              onStartChange={newClients.setDateStart}
              onEndChange={newClients.setDateEnd}
            />
            <ReportLoadButton
              isLoading={newClients.store.isLoading}
              onClick={() => void newClients.load()}
            />
            <div className='relative ml-auto'>
              <select
                value={newClients.store.groupBy}
                onChange={(e) => newClients.setGroupBy(e.target.value as GroupBy)}
                className='appearance-none pl-3.5 pr-9 py-2 rounded-xl border border-border/50 bg-card text-sm text-foreground outline-none focus:border-primary/50 cursor-pointer'
              >
                {groupByOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className='absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none' />
            </div>
            <ReportExportMenu
              onExcelExport={() =>
                exportNewClientsToExcel(newClients.store.items, newClients.countryOf)
              }
              onGoogleExport={() =>
                exportNewClientsToGoogleSheets(newClients.store.items, newClients.countryOf)
              }
            />
          </ReportFilters>

          {newClients.store.error && <ErrorBanner message={newClients.store.error} />}

          {/* Сводка */}
          <StatsGrid
            items={stats}
            className={cn('sm:grid-cols-3', stats.length > 5 ? 'lg:grid-cols-6' : 'lg:grid-cols-5')}
          />

          {/* Графики */}
          {chartData.length > 0 && (
            <div
              className={cn(
                'grid grid-cols-1 gap-4 mb-6',
                paymentCharts.length > 1 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
              )}
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className='p-5 rounded-2xl border border-border/30 bg-card'
              >
                <ReportBarChart
                  data={chartData}
                  title='Новые клиенты'
                  description='по месяцам регистрации'
                  dataKey='clients'
                  name='Клиентов'
                  fill={C.primary}
                  barSize={32}
                  yAxisWidth={20}
                  xTickSize={11}
                  yAllowDecimals={false}
                  yTickFormatter={(value) => String(value)}
                  tooltipContent={<ChartTooltip formatValue={(value) => String(value)} />}
                  className='border-0 p-0 bg-transparent'
                />
              </motion.div>

              {paymentCharts.map((chart, index) => (
                <motion.div
                  key={chart.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                  className='p-5 rounded-2xl border border-border/30 bg-card'
                >
                  <div className='text-sm font-semibold text-foreground mb-1'>
                    Сумма оплат · {chart.label}
                  </div>
                  <div className='text-xs text-muted-foreground mb-4'>
                    по месяцам{chart.currency && `, ${chart.currency}`}
                  </div>
                  <ResponsiveContainer width='100%' height={200}>
                    <AreaChart data={chart.data}>
                      <defs>
                        <linearGradient
                          id={`newClientsGrad-${chart.key}`}
                          x1='0'
                          y1='0'
                          x2='0'
                          y2='1'
                        >
                          <stop offset='0%' stopColor={C.secondary} stopOpacity={0.3} />
                          <stop offset='100%' stopColor={C.secondary} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray='3 3' stroke={C.border} vertical={false} />
                      <XAxis
                        dataKey='label'
                        tick={{ fontSize: 11, fill: C.text }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: C.text }}
                        axisLine={false}
                        tickLine={false}
                        width={38}
                        tickFormatter={formatThousandsTick}
                      />
                      <Tooltip
                        content={
                          <ChartTooltip
                            formatValue={(value) => formatMoney(value, chart.currency)}
                          />
                        }
                        cursor={false}
                      />
                      <Area
                        dataKey='payments'
                        name='Оплаты'
                        stroke={C.secondary}
                        strokeWidth={2}
                        fill={`url(#newClientsGrad-${chart.key})`}
                        dot={{ r: 4, fill: C.secondary, stroke: '#fff', strokeWidth: 2 }}
                        activeDot={{ r: 5, fill: C.secondary, stroke: '#fff', strokeWidth: 2 }}
                        isAnimationActive
                        animationDuration={800}
                        animationEasing='ease-out'
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </motion.div>
              ))}
            </div>
          )}

          {/* Таблица */}
          <AsyncState
            isLoading={newClients.store.isLoading}
            error={null}
            isEmpty={newClients.store.items.length === 0}
          >
            <ReportTable headers={tableHeaders} gapClassName='gap-2'>
              {newClients.grouped().map((group) => (
                <div key={group.key}>
                  <div className='px-4 py-2 bg-muted/10 border-b border-border/10 text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
                    {group.label} — {group.entries.length} кл.
                  </div>
                  {group.entries.map((item) => (
                    <Row
                      key={`${item.id_account}-${item.date_reg}`}
                      item={item}
                      currency={newClients.currencyOf(item)}
                    />
                  ))}
                </div>
              ))}
            </ReportTable>
          </AsyncState>
        </motion.div>
      </div>
    </div>
  );
});
